/**
 * Light/dark toggle.
 *
 * The actual class on <html> is set before first paint by the inline script in layout.tsx (so there
 * is no flash of the wrong theme). This button just flips it and remembers the choice. It reads the
 * live state from the document on mount rather than holding its own truth, so it always matches what
 * the page is actually showing.
 */

import { useEffect, useState } from 'react';

const THEME_KEY = 'lipa.theme';

export function ThemeToggle({ className }: { className?: string }) {
  const [dark, setDark] = useState(false);

  useEffect(() => {
    setDark(document.documentElement.classList.contains('dark'));
  }, []);

  function toggle() {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle('dark', next);
    try {
      localStorage.setItem(THEME_KEY, next ? 'dark' : 'light');
    } catch {
      /* storage blocked: the choice just will not persist */
    }
  }

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      className={
        className ??
        'rounded-lg p-1.5 text-ink-muted transition hover:bg-surface-sunken hover:text-ink'
      }
    >
      {dark ? (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <circle cx="12" cy="12" r="4" strokeWidth={1.8} />
          <path
            strokeLinecap="round"
            strokeWidth={1.8}
            d="M12 2v2m0 16v2M4 12H2m20 0h-2M5.6 5.6 4.2 4.2m15.6 15.6-1.4-1.4M18.4 5.6l1.4-1.4M4.2 19.8l1.4-1.4"
          />
        </svg>
      ) : (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.8}
            d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z"
          />
        </svg>
      )}
    </button>
  );
}
